import React from 'react';
import { StepComponent } from '@service-victoria/component-library/components/Wizard/props';
import Spinner from '@service-victoria/component-library/components/Icons/Spinner';
import * as Interfaces from 'interfaces';
import * as Styled from '../styled';
import { isBorderResident, isExemptionApplication } from './travel-reasons';
import { TravelZoneSelector } from 'Components/TravelZoneSelector';
import { StorageKeys } from 'utils/constants';
import { useValidatePreviousStep } from 'hooks';
import { TravelReasonSuccess } from 'interfaces/store';
import useTravelReasons from 'hooks/useTravelReasons';
import AssessTechnicalError from './components/AssessTechnicalError';
import ExemptionForm from './components/ExemptionForm';
import deepmerge from 'deepmerge';
import TravelDetailsContent from './components/TravelDetailsContent';
import { TravelPermitPreamble } from './components/TravelPermitPreamble';
import BorderNotice from './components/BorderNotice';
import useAssessStepConstraints from './hooks';
import { TravelPermitForm } from './components/TravelPermitForm';
import { TravelReasonSelector } from './components/TravelReasonSelector';
import { VaccinationStatus } from './components/VaccinationStatus';
import type { DeepPartial } from 'types/globals';
import { isReturnExemption } from './travel-reasons';

export type ComponentProps = StepComponent<Pick<Interfaces.Common.FormValues, 'assess'>>;

const Assess: React.FC<ComponentProps> = ({ context }) => {
  const appContext = context.appStates[StorageKeys.State] as Interfaces.Store.AppContext;
  const { travelReasons } = appContext.currentState;
  const { fetch } = useTravelReasons();

  const {
    form: { values, setFieldValue, setValues }
  } = context;

  // Validate that user filled previous step forms
  useValidatePreviousStep('assess', context);
  const {
    assess: { reasons: reasonCode, travelZone }
  } = values as Interfaces.Common.FormValues;

  const setDeltaValues = (deltas: DeepPartial<Pick<Interfaces.Common.FormValues, 'assess'>>) =>
    setValues(deepmerge(values, deltas));

  const selectZone = (zone: string) => {
    const reasonForSelect =
      (travelReasons as TravelReasonSuccess).reasons.filter(reason => reason.zone === zone) || [];
    if (reasonForSelect.length === 1) {
      // auto-set the travel reason code in the form if it's the only one (green/orange zones currently don't have nested reasons)
      setFieldValue('assess.reasons', reasonForSelect[0].value, false);
    } else {
      setFieldValue('assess.reasons', '', false);
    }
    // Apparently simply validating the field doesn't actually trigger a re-render of the field.
    //  For some ungodly reason Formik seems to go stale for the nested component
    //  Dear God please let me refactor this garbage away and use react-hook-form instead...
    setTimeout(() => {
      setFieldValue('assess.Depart.date', values.assess.Depart.date, true);
    }, 0);
  };

  useAssessStepConstraints(values, setFieldValue, setDeltaValues);

  if (travelReasons.status === 'error') {
    return <AssessTechnicalError onClick={fetch} />;
  } else if (travelReasons.status === 'pending') {
    return (
      <Styled.Wrapper isHideFormOptions>
        <Styled.LoadingWrapper>
          <Spinner />
        </Styled.LoadingWrapper>
      </Styled.Wrapper>
    );
  }

  return (
    <Styled.Wrapper isHideFormOptions={isBorderResident(reasonCode)}>
      <TravelDetailsContent />
      <Styled.Spacer32 />
      <TravelPermitPreamble />
      <Styled.Spacer32 />
      <Styled.Spacer32 />
      <TravelZoneSelector
        name='assess.travelZone'
        onSelect={selectZone}
        labelID='assess.reasons.label'
        travelReasons={travelReasons}
      />
      <TravelReasonSelector />
      <VaccinationStatus travelZone={travelZone} />
      {isExemptionApplication(reasonCode) || isReturnExemption(reasonCode) ? (
        <ExemptionForm />
      ) : null}
      {isBorderResident(reasonCode) ? (
        <BorderNotice />
      ) : (
        <TravelPermitForm setDeltaValues={setDeltaValues} values={values} />
      )}
    </Styled.Wrapper>
  );
};

export default React.memo(Assess);
